/**
 * Mirror rules in api/src/helpers/class-grades-catalog.js (keep in sync).
 */
export function normalizeClassGradeCodeInput(raw) {
  return String(raw ?? "")
    .trim()
    .replace(/\s+/g, "")
    .toUpperCase();
}

export function isValidCustomClassGradeCode(normalizedUpper) {
  const c = normalizedUpper;
  if (!c || c.length < 2 || c.length > 32) return false;
  if (/[^A-Z0-9-]/.test(c)) return false;
  if (/-{2,}/.test(c)) return false;
  if (c.startsWith("-") || c.endsWith("-")) return false;
  if (!/[A-Z]/.test(c)) return false;

  if (/^[PS][1-6]/.test(c)) {
    return /^[PS]([1-6])(?:[A-Z]+(?:-[A-Z]+)*)?$/.test(c);
  }

  if (/^N[1-3]/.test(c)) {
    return /^N([1-3])(?:[A-Z]+(?:-[A-Z]+)*)?$/.test(c);
  }

  return /^([A-Z0-9]+)(-[A-Z0-9]+)*$/.test(c);
}

/** Matches api/src/helpers/class-grades-catalog.js error hint for UX. */
export function customClassGradeCodeHintShort() {
  return "P/S: class 1–6 + optional letter streams (e.g. S1A-C, P1A-B). Nursery (pre-primary): N1–N3 + optional streams (e.g. N2A-B).";
}

/** Pre-primary nursery: N1, N2, N3 (+ same stream suffix shape as P/S). */
export function isNurseryStyleClassGradeCode(normalizedUpper) {
  const c = normalizedUpper;
  return typeof c === "string" && /^N([1-3])(?:[A-Z]+(?:-[A-Z]+)*)?$/.test(c);
}

/** P1 … P6 and stream variants — same shape as backend validation for P*. */
export function isPrimaryStyleClassGradeCode(normalizedUpper) {
  const c = normalizedUpper;
  return typeof c === "string" && /^P([1-6])(?:[A-Z]+(?:-[A-Z]+)*)?$/.test(c);
}

/** S1 … S6 and stream variants. */
export function isSecondaryStyleClassGradeCode(normalizedUpper) {
  const c = normalizedUpper;
  return typeof c === "string" && /^S([1-6])(?:[A-Z]+(?:-[A-Z]+)*)?$/.test(c);
}

/**
 * Map SchoolLevel row → which class-grade family is allowed when assigning.
 * Mirrors names like PRIMARY / SECONDARY / PRE_PRIMARY from `[SchoolLevels]`.
 */
export function schoolLevelNameToGradeTier(level) {
  if (!level?.name) return null;
  const n = String(level.name).toUpperCase();
  if (n.includes("PRE") && n.includes("PRIMARY")) return "pre_primary";
  if (n.includes("SECONDARY")) return "secondary";
  if (n.includes("PRIMARY")) return "primary";
  return null;
}

/**
 * @param {string} normalizedUpper
 * @param {'primary'|'secondary'|'pre_primary'|null} tier
 */
export function classGradeMatchesSchoolLevelTier(normalizedUpper, tier) {
  if (!tier) return true;
  const p = isPrimaryStyleClassGradeCode(normalizedUpper);
  const s = isSecondaryStyleClassGradeCode(normalizedUpper);
  if (tier === "secondary") return s;
  if (tier === "primary") return p;
  /** Pre-primary: nursery + e.g. PRE-PRIMARY — never catalog P1–P6 nor S1–S6. */
  if (tier === "pre_primary") return !p && !s;
  return true;
}

export function schoolTierGradeHint(tier) {
  if (tier === "secondary")
    return "Only senior-style grades (S1–S6, e.g. S1A-C) for this level.";
  if (tier === "primary")
    return "Only primary-style grades (P1–P6, e.g. P1A-B) for this level.";
  if (tier === "pre_primary")
    return "Pre-primary only: nursery N1–N3 (and streams); P1–P6 and S1–S6 are hidden. Optional codes like PRE-PRIMARY still allowed.";
  return null;
}
